import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Crown, Calendar, X, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const fmtBRL = (n) => `R$ ${Number(n || 0).toFixed(2).replace('.', ',')}`;

/**
 * UpgradeModal — shown when a free user hits the monthly consultation cap.
 *
 * Visual: full-screen overlay on mobile, centered card on desktop. Two plan
 * options side by side (Mensal / Anual destaque) plus a soft "Lembrar depois"
 * link that simply closes the modal.
 *
 * Props:
 *  - open  : boolean controlling visibility
 *  - onClose: () => void invoked by the X button or "Lembrar depois"
 *  - reason: optional string shown above the title (e.g. backend 402 detail)
 */
const UpgradeModal = ({ open, onClose, reason }) => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState({ mensal: null, anual: null });

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    axios
      .get(`${API}/api/plans/`, { timeout: 8000 })
      .then((resp) => {
        if (cancelled) return;
        const list = resp.data?.plans || [];
        setPlans({
          mensal: list.find((p) => p.id === 'mensal') || null,
          anual: list.find((p) => p.id === 'anual') || null,
        });
      })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [open]);

  // Close on ESC for a11y
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const goCheckout = (planId) => {
    onClose?.();
    navigate(`/checkout/${planId}`);
  };

  const { mensal, anual } = plans;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/55 px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-modal-title"
      data-testid="upgrade-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden"
        style={{ paddingBottom: 'var(--safe-bottom, 0px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br from-[#006030] to-[#004020] px-5 py-5 sm:px-7 sm:py-6 text-white">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
            aria-label="Fechar"
            data-testid="upgrade-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 mb-1">
            <Lock className="w-4 h-4" />
            <span className="text-[11px] font-bold uppercase tracking-wider opacity-90">
              Limite gratuito atingido
            </span>
          </div>
          <h2
            id="upgrade-modal-title"
            className="text-xl sm:text-2xl font-black leading-tight"
          >
            Você atingiu seu limite mensal.
          </h2>
          <p className="text-sm text-white/85 mt-1">
            Assine o Plano Completo e consulte candidatos sem limite.
          </p>
          {reason && (
            <p className="text-[11px] text-white/70 mt-2" data-testid="upgrade-modal-reason">
              {reason}
            </p>
          )}
        </div>

        {/* Plans */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 sm:p-6 bg-[#F8FAFC]">
          {/* Mensal */}
          {mensal && (
            <button
              type="button"
              onClick={() => goCheckout('mensal')}
              className="group text-left p-4 bg-white border border-[#E2E8F0] rounded-md hover:border-[#0F1C2E] transition-colors"
              data-testid="upgrade-modal-mensal"
            >
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-[#475569]" />
                <span className="text-xs font-bold text-[#475569] uppercase tracking-wider">
                  Mensal
                </span>
              </div>
              <p className="text-2xl font-black text-[#0F1C2E]">
                {fmtBRL(mensal.preco)}
                <span className="text-xs text-[#94A3B8] font-normal">/mês</span>
              </p>
              <p className="text-[11px] text-[#94A3B8] mt-1">cancele quando quiser</p>
              <p className="mt-3 text-[12px] font-bold text-[#006030] group-hover:underline">
                Assinar agora →
              </p>
            </button>
          )}

          {/* Anual — destaque */}
          {anual && (
            <button
              type="button"
              onClick={() => goCheckout('anual')}
              className="group text-left p-4 bg-white border-2 border-[#006030] rounded-md hover:bg-[#F0FDF4] transition-colors relative"
              data-testid="upgrade-modal-anual"
            >
              {anual.badge && (
                <span className="absolute -top-2 right-3 inline-flex items-center bg-[#F59E0B] text-white text-[10px] font-bold px-2 py-0.5 rounded">
                  {anual.badge}
                </span>
              )}
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-4 h-4 text-[#006030]" />
                <span className="text-xs font-bold text-[#006030] uppercase tracking-wider">
                  Anual · Melhor valor
                </span>
              </div>
              <p className="text-2xl font-black text-[#006030]">
                {fmtBRL(anual.preco)}
                <span className="text-xs text-[#94A3B8] font-normal">/ano</span>
              </p>
              {anual.preco_mensal_equivalente_label && (
                <p className="text-[11px] text-[#94A3B8] mt-1">
                  ≈ {anual.preco_mensal_equivalente_label}
                </p>
              )}
              {anual.economia_label && (
                <p className="text-[11px] font-bold text-[#006030] mt-1">
                  {anual.economia_label}
                </p>
              )}
              <p className="mt-3 text-[12px] font-bold text-[#006030] group-hover:underline">
                Assinar agora →
              </p>
            </button>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-4 sm:px-6 pb-5 pt-1 bg-[#F8FAFC] flex flex-col sm:flex-row items-center justify-between gap-3">
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-[#475569] hover:text-[#0F1C2E] underline-offset-2 hover:underline"
            data-testid="upgrade-modal-later"
          >
            Lembrar depois
          </button>
          <Button
            type="button"
            onClick={() => goCheckout('anual')}
            className="bg-[#006030] hover:bg-[#004020] text-white font-bold w-full sm:w-auto"
            data-testid="upgrade-modal-cta"
          >
            <Crown className="w-4 h-4 mr-2" /> Assinar agora
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
